import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { cn } from '@/core/lib/utils';
import type { ProjectHealthDto } from '../../types';

interface ProjectHealthCardProps {
  project: ProjectHealthDto;
}

const STATUS_STYLES: Record<string, string> = {
  Active: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400',
  OnHold: 'bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400',
  Completed: 'bg-muted text-muted-foreground',
};

function formatHours(decimal: number): string {
  const h = Math.floor(decimal);
  const m = Math.round((decimal - h) * 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export function ProjectHealthCard({ project }: ProjectHealthCardProps) {
  const { t } = useTranslation('analytics');

  const statusStyle = project.projectStatus
    ? (STATUS_STYLES[project.projectStatus] ?? STATUS_STYLES.Completed)
    : STATUS_STYLES.Completed;

  const progressPct = Math.min(100, project.progressPercentage);
  const projectColor = project.projectColor ?? '#6366f1';

  return (
    <Card data-testid={`analytics-project-health-card-${project.projectId ?? 'unknown'}`}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span
              className="h-3 w-3 shrink-0 rounded-full"
              style={{ backgroundColor: projectColor }}
            />
            <span className="text-sm font-semibold leading-snug">{project.projectName ?? '—'}</span>
          </div>
          {project.projectStatus && (
            <span
              className={cn('shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium', statusStyle)}
            >
              {t(`projectHealth.status.${project.projectStatus}`)}
            </span>
          )}
        </div>

        {/* Progress bar */}
        <div className="mb-3">
          <div className="mb-1.5 flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{t('projectHealth.progress')}</span>
            <span className="font-semibold" style={{ color: projectColor }}>
              {progressPct.toFixed(0)}%
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${progressPct}%`, backgroundColor: projectColor }}
            />
          </div>
        </div>

        {/* Stats: Total / Listas / Activas / Atraso */}
        <div className="mb-3 grid grid-cols-4 text-center">
          <div>
            <p className="text-lg font-bold">{project.totalTasks}</p>
            <p className="text-xs text-muted-foreground">{t('projectHealth.tasks.total')}</p>
          </div>
          <div>
            <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
              {project.doneTasks}
            </p>
            <p className="text-xs text-muted-foreground">{t('projectHealth.tasks.doneShort')}</p>
          </div>
          <div>
            <p className="text-lg font-bold text-cyan-600 dark:text-cyan-400">
              {project.inProgressTasks}
            </p>
            <p className="text-xs text-muted-foreground">{t('projectHealth.tasks.activeShort')}</p>
          </div>
          <div>
            <p className="text-lg font-bold text-destructive">{project.overdueTasks}</p>
            <p className="text-xs text-muted-foreground">{t('projectHealth.tasks.overdueShort')}</p>
          </div>
        </div>

        {/* Efficiency footer */}
        <div className="flex items-center gap-1.5 border-t pt-2.5 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5 shrink-0" />
          <span>
            {t('projectHealth.efficiency')}:{' '}
            <strong className="text-primary">{project.efficiencyPercentage.toFixed(0)}%</strong> (
            {formatHours(project.totalEstimatedHours)} est. /{' '}
            {formatHours(project.totalTrackedHours)} real)
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
